import * as uuid from 'uuid';
import AdhocShiftSelectForm, { ShiftCreationType, ShiftsListType } from 'client/components/forms/AdhocShiftSelectForm';
import ApplicationError from 'client/components/ApplicationError';
import Col from 'client/components/Col';
import Container from 'client/components/Container';
import FlexApi, { ShiftBooking } from 'utils/FlexApi';
import React, { useEffect } from 'react';
import Row from 'client/components/Row';
import qs from 'qs';
import { Card, Text } from '@indeed/ifl-components';
import { DateTime } from 'luxon';
import { HELP_LINK, MAX_SHIFTS_TO_COPY_FROM } from 'constants/index';
import { datadogRum } from '@datadog/browser-rum';
import { useClientConfig } from 'client/context/ClientConfigContext';

const fromDate = DateTime.local().minus({ days: 14 }).startOf('day').toJSDate();

const toDate = DateTime.local().minus({ days: 1 }).endOf('day').toJSDate();

/*
    Sort by descending shift start time
*/
export const shiftSorter = (a: ShiftBooking, b: ShiftBooking): number => {
    return b.shiftStart.valueOf() - a.shiftStart.valueOf();
};

const AdHocShiftsCreatePage = (): JSX.Element => {
    const [loading, setLoading] = React.useState<boolean>(true);
    const [pastShiftsList, setPastShiftsList] = React.useState<ShiftsListType[]>([]);

    const clientConfig = useClientConfig();
    const escapeUri = clientConfig['escape-uri'] as string;

    useEffect(() => {
        if (!escapeUri) {
            return;
        }
        if (!clientConfig['worker-email'] || !clientConfig['country-code']) {
            return;
        }
        const requestId = uuid.v4();
        const flexInstance =
            clientConfig['country-code'] === 'US'
                ? { currency: 'USD', currencySymbol: '$', countryCode: 'US' }
                : { currency: 'GBP', currencySymbol: '£', countryCode: 'GB' };
        const flexApi = new FlexApi(clientConfig.appUrl!, flexInstance.countryCode);
        flexApi
            .getShiftBookings(fromDate!, toDate!, { requestId })
            .then((shifts) => {
                shifts.sort(shiftSorter);
                if (pastShiftsList.length === 0) {
                    const uniqueShiftsSet = new Set<string>();
                    const uniqueShiftsList: ShiftsListType[] = [];
                    shifts.forEach((shift) => {
                        const shiftForCreation: ShiftCreationType = {
                            client: shift.client,
                            timezone: shift.timezone,
                            payRate: shift.payRate.amount.toString(),
                            breakDuration: shift.breakDuration,
                            currency: shift.payRate.currency,
                            role: shift.roleName,
                            venue: shift.venueName,
                        };
                        if (
                            !uniqueShiftsSet.has(JSON.stringify(shiftForCreation)) &&
                            uniqueShiftsList.length < MAX_SHIFTS_TO_COPY_FROM
                        ) {
                            uniqueShiftsSet.add(JSON.stringify(shiftForCreation));
                            const currencySymbol = flexInstance!.currencySymbol;
                            const payRate = Number.isInteger(shift.payRate.amount)
                                ? shift.payRate.amount
                                : shift.payRate.amount.toFixed(2);
                            const breakText =
                                shift.breakDuration > 0 ? `(${shift.breakDuration / 60} min break)` : '(no break)';
                            uniqueShiftsList.push({
                                children: (
                                    <Card>
                                        {shift.client} <br />
                                        {shift.venueName} <br />
                                        {shift.roleName} <br /> {currencySymbol}
                                        {payRate}/hr {breakText}
                                    </Card>
                                ),
                                // Appending shiftBookingId later to eliminate it from the uniqueness check
                                value: JSON.stringify({
                                    ...shiftForCreation,
                                    shiftBookingId: shift.shiftBookingId,
                                }),
                            });
                        }
                    });
                    setPastShiftsList(uniqueShiftsList);
                    setLoading(false);
                }
            })
            .catch((e) => {
                datadogRum.addError(e, { tags: { requestId } });
                setPastShiftsList([]);
                setLoading(false);
            });
    }, [escapeUri, clientConfig, pastShiftsList.length]);

    if (!escapeUri) {
        return <ApplicationError />;
    }
    if (!clientConfig['worker-email'] || !clientConfig['country-code']) {
        const errorParams = {
            error: 'Authorization failure',
        };
        window.location.replace(`${escapeUri}?${qs.stringify(errorParams)}`);
    }

    return (
        <Container sx={{ padding: 0 }}>
            <Row>
                <Col>
                    {pastShiftsList.length === 0 && !loading && (
                        <Text variant="contrast" as="p" level={2}>
                            You do not have any eligible shifts that can be added. Jobs enabled by clients will be
                            listed here.
                        </Text>
                    )}
                    <Text variant="contrast" as="p" level={2}>
                        Only recently worked jobs may be added as an upcoming shift. Flex will review added shifts, and
                        any unworked shifts will lead to potential penalty. For any issues please contact{' '}
                        <a href={HELP_LINK}>IndeedFlex Support.</a>
                    </Text>
                </Col>
            </Row>
            {pastShiftsList.length > 0 && (
                <AdhocShiftSelectForm
                    clientConfig={clientConfig}
                    pastShiftsList={pastShiftsList}
                    escapeUri={escapeUri}
                />
            )}
        </Container>
    );
};

export default AdHocShiftsCreatePage;
